(() => {
  'use strict';

  if (window.TitanMotion) {
    window.TitanMotion.collect(document);
    return;
  }

  const root = document.documentElement;
  const body = document.body;
  const reduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const drivers = new Set();
  let frameId = 0;
  let forceFrame = true;

  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const designMode = () => root.classList.contains('shopify-design-mode') || Boolean(window.Shopify?.designMode);
  const viewportHeight = () => window.visualViewport?.height || window.innerHeight || 1;

  function context() {
    return {
      clamp,
      designMode: designMode(),
      force: forceFrame,
      height: viewportHeight(),
      reduced: reduceQuery.matches,
      scrollY: window.scrollY || 0,
      width: window.innerWidth || root.clientWidth || 1,
    };
  }

  function render() {
    frameId = 0;
    const state = context();
    let continueAnimation = false;
    forceFrame = false;

    drivers.forEach((driver) => {
      try {
        if (driver.update?.(state) === true) continueAnimation = true;
      } catch (error) {
        console.error('Titan Walls motion driver failed.', error);
      }
    });

    if (continueAnimation) schedule();
  }

  function schedule(force = false) {
    forceFrame = forceFrame || force;
    if (!frameId) frameId = requestAnimationFrame(render);
  }

  function register(driver) {
    if (!driver || drivers.has(driver)) return driver;
    drivers.add(driver);
    driver.collect?.(document);
    schedule(true);
    return driver;
  }

  function unregister(driver) {
    if (!driver || !drivers.has(driver)) return;
    driver.destroy?.();
    drivers.delete(driver);
    schedule(true);
  }

  function collect(scope = document) {
    drivers.forEach((driver) => driver.collect?.(scope));
    schedule(true);
  }

  function remove(scope) {
    drivers.forEach((driver) => driver.remove?.(scope));
    schedule(true);
  }

  window.TitanMotion = {
    clamp,
    collect,
    register,
    remove,
    schedule,
    unregister,
  };

  function hardenExternalLinks(scope = document) {
    scope.querySelectorAll?.('a[href^="http"]').forEach((link) => {
      try {
        const url = new URL(link.href, location.href);
        if (url.hostname === location.hostname) return;
        const rel = new Set((link.rel || '').split(/\s+/).filter(Boolean));
        rel.add('noopener');
        link.rel = Array.from(rel).join(' ');
      } catch (_) {}
    });
  }

  const headers = new Map();
  const headerDriver = {
    collect(scope = document) {
      const nodes = [];
      if (scope.matches?.('[data-twhead]')) nodes.push(scope);
      scope.querySelectorAll?.('[data-twhead]').forEach((node) => nodes.push(node));
      nodes.forEach((header) => this.mount(header));
    },

    mount(header) {
      if (headers.has(header)) return;
      const drawer = header.querySelector('[data-twhead-drawer]');
      const shade = header.querySelector('[data-twhead-shade]');
      const openers = Array.from(header.querySelectorAll('[data-twhead-open]'));
      const closers = Array.from(header.querySelectorAll('[data-twhead-close]'));
      if (!drawer || !shade || !openers.length) return;

      const controller = new AbortController();
      const signal = controller.signal;
      const sectionRoot = header.closest('[id^="shopify-section-"]') || header.parentElement;
      const record = {
        controller,
        drawer,
        enter: Math.max(0, Number.parseInt(header.dataset.enter || '80', 10)),
        header,
        lastFocus: null,
        open: false,
        scrolled: false,
        scrollY: 0,
        sectionRoot,
        shade,
      };
      headers.set(header, record);

      const focusableSelector = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),summary,[tabindex]:not([tabindex="-1"])';
      const focusable = () => Array.from(drawer.querySelectorAll(focusableSelector)).filter((node) => {
        const style = getComputedStyle(node);
        return style.visibility !== 'hidden' && style.display !== 'none';
      });
      const background = () => [
        header.querySelector('.twhead__shell'),
        header.querySelector('.twhead__bar'),
        document.getElementById('MainContent'),
        document.querySelector('[id^="shopify-section-footer-group"]'),
      ].filter(Boolean);

      const setBackgroundInert = (inert) => {
        background().forEach((node) => {
          node.toggleAttribute('inert', inert);
          if (inert) node.setAttribute('aria-hidden', 'true');
          else node.removeAttribute('aria-hidden');
        });
      };

      const unlock = () => {
        body.classList.remove('tw-scroll-lock');
        body.style.top = '';
        window.scrollTo(0, record.scrollY);
      };
      const close = (restoreFocus = true) => {
        if (!record.open) return;
        record.open = false;
        drawer.setAttribute('aria-hidden', 'true');
        drawer.setAttribute('inert', '');
        drawer.removeAttribute('data-open');
        shade.removeAttribute('data-open');
        openers.forEach((button) => button.setAttribute('aria-expanded', 'false'));
        setBackgroundInert(false);
        unlock();
        if (restoreFocus && record.lastFocus?.isConnected) record.lastFocus.focus();
      };
      const open = () => {
        if (record.open) return;
        record.open = true;
        record.lastFocus = document.activeElement;
        record.scrollY = window.scrollY || 0;
        body.classList.add('tw-scroll-lock');
        body.style.top = `${-record.scrollY}px`;
        drawer.removeAttribute('inert');
        drawer.setAttribute('aria-hidden', 'false');
        drawer.setAttribute('data-open', 'true');
        shade.setAttribute('data-open', 'true');
        openers.forEach((button) => button.setAttribute('aria-expanded', 'true'));
        setBackgroundInert(true);
        requestAnimationFrame(() => (closers[0] || focusable()[0] || drawer).focus());
      };

      openers.forEach((button) => button.addEventListener('click', open, { signal }));
      closers.forEach((button) => button.addEventListener('click', () => close(), { signal }));
      shade.addEventListener('click', () => close(), { signal });
      drawer.addEventListener('click', (event) => {
        if (event.target.closest('a[href]')) close(false);
      }, { signal });
      header.addEventListener('keydown', (event) => {
        if (!record.open) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          close();
          return;
        }
        if (event.key !== 'Tab') return;
        const nodes = focusable();
        if (!nodes.length) {
          event.preventDefault();
          drawer.focus();
          return;
        }
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }, { signal });
      record.close = close;
    },

    update({ scrollY }) {
      headers.forEach((record, header) => {
        if (!header.isConnected) {
          this.destroy(header);
          return;
        }
        if (header.dataset.shrink !== 'true') return;
        const exit = Math.floor(record.enter * 0.45);
        const next = record.scrolled ? scrollY > exit : scrollY >= record.enter;
        if (next === record.scrolled) return;
        record.scrolled = next;
        header.setAttribute('data-scrolled', String(next));
        record.sectionRoot?.setAttribute('data-twhead-scrolled', String(next));
      });
      return false;
    },

    destroy(header) {
      const record = headers.get(header);
      if (!record) return;
      record.close?.(false);
      record.controller.abort();
      headers.delete(header);
    },

    remove(scope) {
      if (scope.matches?.('[data-twhead]')) this.destroy(scope);
      scope.querySelectorAll?.('[data-twhead]').forEach((header) => this.destroy(header));
    },
  };

  register(headerDriver);
  hardenExternalLinks(document);

  const onViewportChange = () => schedule(true);
  addEventListener('scroll', () => schedule(), { passive: true });
  addEventListener('resize', onViewportChange, { passive: true });
  addEventListener('orientationchange', onViewportChange, { passive: true });
  addEventListener('pageshow', () => schedule(true), { passive: true });
  window.visualViewport?.addEventListener('resize', onViewportChange, { passive: true });
  reduceQuery.addEventListener?.('change', () => schedule(true));

  document.addEventListener('shopify:section:load', (event) => {
    hardenExternalLinks(event.target);
    collect(event.target);
  });
  document.addEventListener('shopify:section:unload', (event) => remove(event.target));
  document.addEventListener('shopify:section:reorder', () => collect(document));
  document.addEventListener('shopify:section:select', (event) => collect(event.target));
  document.addEventListener('shopify:section:deselect', () => schedule(true));
  document.addEventListener('shopify:block:select', () => schedule(true));

  root.classList.add('tw-motion-ready');
  schedule(true);
})();
